import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MessageCircle, Star, MapPin, ArrowDown } from "lucide-react";
import { ParticleField } from "./ParticleField";
import { waLink, IMG, BRAND } from "../../lib/data";

const lineWrap = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } } };
const line = {
  hidden: { y: "110%" },
  show: { y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const HEAD = ["Solusi Teknologi", "& Keamanan untuk", "Rumah dan Bisnis Anda"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="beranda"
      ref={ref}
      className="relative min-h-[100svh] overflow-hidden bg-[#071A33] pt-[74px]"
      data-testid="hero"
    >
      <div className="absolute inset-0 opacity-70 [background:radial-gradient(60%_50%_at_20%_20%,rgba(11,99,246,0.35),transparent_60%),radial-gradient(50%_50%_at_90%_10%,rgba(22,199,232,0.25),transparent_60%)]" />
      <ParticleField className="absolute inset-0 h-full w-full" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-10 items-center min-h-[calc(100svh-74px)] py-14">
        <motion.div style={{ opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs text-white/80"
          >
            <span className="flex text-[#16C7E8]">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}
            </span>
            <span className="font-semibold text-white">5.0</span> Google Reviews
          </motion.div>

          <motion.h1
            variants={lineWrap}
            initial="hidden"
            animate="show"
            className="mt-6 font-heading font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-[4.1rem] leading-[1.05]"
          >
            {HEAD.map((t, i) => (
              <span key={i} className="block overflow-hidden py-[1px]">
                <motion.span variants={line} className="block">
                  {i === 0 ? (
                    <span className="bg-gradient-to-r from-[#16C7E8] to-[#0B63F6] bg-clip-text text-transparent">
                      {t}
                    </span>
                  ) : t}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-6 max-w-xl text-white/70 text-base md:text-lg leading-relaxed"
          >
            Jual & pasang CCTV, parabola, WiFi, hardware, software, serta solusi
            jaringan dengan layanan profesional dari SATCOM MEDIA TEKNOLOGI.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#0B63F6] hover:scale-[1.03] hover:bg-[#16C7E8] text-white font-semibold px-7 py-4 transition-all shadow-xl shadow-[#0B63F6]/40"
            >
              <MessageCircle className="h-5 w-5" />
              Konsultasi via WhatsApp
            </a>
            <a
              href="#layanan"
              data-testid="hero-services-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur text-white font-semibold px-7 py-4 hover:bg-white/10 transition-colors"
            >
              Lihat Layanan
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="mt-7 inline-flex items-center gap-2 text-sm text-white/60"
          >
            <MapPin className="h-4 w-4 text-[#16C7E8]" />
            Melayani {BRAND.area}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: imgY, scale: imgScale }} className="relative hidden lg:block">
          <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-[#0B63F6]/30 to-[#16C7E8]/20 blur-2xl" />
          <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl">
            <img
              src={IMG.hero}
              alt="Kombinasi perangkat teknologi SATCOM: CCTV, parabola, router WiFi, dan hardware komputer"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
        </motion.div>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.div>
    </section>
  );
};
